// Localization (§8): English first, plus a Pidgin option. Keys cover the HUD, zone prompts,
// phone chrome, settings and account; untranslated keys fall back to English.
import { signal } from "@preact/signals";
import { flag } from "@degen/shared/flags";

export type Lang = "en" | "pcm";
const KEY = "degen-world:lang";

function readLang(): Lang {
  try {
    return localStorage.getItem(KEY) === "pcm" ? "pcm" : "en";
  } catch {
    return "en";
  }
}

export const lang = signal<Lang>(readLang());

export function setLang(l: Lang) {
  lang.value = l;
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* ignore */
  }
  document.documentElement.lang = l === "pcm" ? "pcm" : "en";
}

const EN = {
  "hud.followers": "Followers",
  "hud.day": "Day",
  "zone.enter": "🚪 Go inside",
  "zone.exit": "🚪 Leave",
  "zone.sleep": "😴 Sleep",
  "zone.laylow": "🫥 Lay low",
  "zone.grass": "🌱 Touch grass",
  "zone.chirp": "🐦 Open Chirp",
  "zone.pumpr": "📈 Open Pumpr",
  "zone.farm": "🌾 Open Farm",
  "zone.cars": "🚗 Browse cars",
  "zone.drive": "🚗 Drive",
  "zone.park": "🅿️ Park & get out",
  "zone.testdrive": "🏁 Test drive (90s)",
  "zone.vano": "🤖 Open Vano",
  "zone.hackathon": "🧑‍💻 Hackathons",
  "zone.gadgets": "📱 Shop phones",
  "zone.events": "🎤 What's on",
  "phone.home": "Home screen",
  "phone.back": "Back",
  "phone.minimize": "Minimize phone",
  "settings.language": "Language",
  "settings.textSize": "Text size",
  "settings.colorblind": "Colorblind-safe up/down colors",
  "settings.reducedMotion": "Reduced motion",
  "settings.music": "Music by district",
  "settings.volume": "Volume",
  "account.title": "Account & cloud save",
  "account.why": "Sign in with your email to save your progress across devices, join shared worlds and get on the leaderboards. No wallet, no real money.",
  "account.continueEmail": "Continue with email",
  "account.signIn": "Sign in",
  "account.signOut": "Sign out",
  "account.offline": "Offline mode: your save lives on this device.",
  "toast.slept": "Slept like a baby. gm, it's day {day} ☀️",
  "toast.grass": "🌱 You touched grass. Heat down, trust up. Wild.",
  "toast.coffee": "☕ Deep breath. Heat cools a little.",
  "quests.title": "Quests & achievements",
  "perps.title": "Perps (DGN margin)",
} as const;

export type TKey = keyof typeof EN;

const PCM: Partial<Record<TKey, string>> = {
  "hud.followers": "Pipo wey follow",
  "hud.day": "Day",
  "zone.enter": "🚪 Enter inside",
  "zone.exit": "🚪 Comot",
  "zone.sleep": "😴 Go sleep",
  "zone.laylow": "🫥 Lie low small",
  "zone.grass": "🌱 Go touch grass",
  "zone.chirp": "🐦 Open Chirp",
  "zone.pumpr": "📈 Open Pumpr",
  "zone.farm": "🌾 Open Farm",
  "zone.cars": "🚗 Check moto dem",
  "zone.drive": "🚗 Drive am",
  "zone.park": "🅿️ Park am, comot",
  "zone.testdrive": "🏁 Test am small (90s)",
  "zone.vano": "🤖 Open Vano",
  "zone.hackathon": "🧑‍💻 Hackathon dem",
  "zone.gadgets": "📱 Buy phone",
  "zone.events": "🎤 Wetin dey happen",
  "phone.home": "Home screen",
  "phone.back": "Go back",
  "phone.minimize": "Keep phone",
  "settings.language": "Language",
  "settings.textSize": "How big di writing",
  "settings.colorblind": "Colors wey colorblind fit see",
  "settings.reducedMotion": "Make am no shake too much",
  "settings.music": "Music for each area",
  "settings.volume": "Volume",
  "account.title": "Account & cloud save",
  "account.why": "Sign in with your email make your progress follow you for any device, join di shared world, enter leaderboard. No wallet, no real money.",
  "account.continueEmail": "Continue with email",
  "account.signIn": "Sign in",
  "account.signOut": "Sign out",
  "account.offline": "Offline: your save dey only for dis device.",
  "toast.slept": "You sleep well well. gm, na day {day} be dis ☀️",
  "toast.grass": "🌱 You don touch grass. Heat don cool, trust don rise.",
  "toast.coffee": "☕ Take am easy. Heat don cool small.",
  "quests.title": "Quests & achievement dem",
  "perps.title": "Perps (DGN margin)",
};

export function t(key: TKey, vars?: Record<string, string | number>): string {
  const useP = lang.value === "pcm" && flag("pidgin");
  let s: string = (useP ? PCM[key] : undefined) ?? EN[key];
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return s;
}

export function hasKey(k: string): k is TKey {
  return k in EN;
}
